import { useNavigate, useSearch } from "@tanstack/react-router";
import type { FilterSpec } from "@/components/data";
import { applyFilters, parseFilters, type FilterValues } from "@/lib/filters";
import type { Entity, EntityFilter, Status } from "./api";
import { ENTITIES, RUN_ENTITIES } from "./format";

export const ENTITY_SPEC: FilterSpec = { name: "entity", label: "Entity", kind: "choice", anyLabel: "All entities", options: [
  { value: "LTD", label: `${ENTITIES.LTD.name} (GBP)` }, { value: "OU", label: `${ENTITIES.OU.name} (EUR)` },
  { value: "LLC", label: `${ENTITIES.LLC.name} (AED)` }, { value: "US_LLC", label: `${ENTITIES.US_LLC.name} (USD)` },
] };
export const RUN_ENTITY_SPEC: FilterSpec = { name: "entity", label: "Entity", kind: "choice", anyLabel: "All entities", options: [
  { value: "LTD", label: `${ENTITIES.LTD.name} (GBP)` }, { value: "OU", label: `${ENTITIES.OU.name} (EUR)` },
  { value: "LLC", label: `${ENTITIES.LLC.name} (AED)` },
] };
export const DEFAULT_STATUSES: Status[] = ["Planned", "Draft", "Computed", "Awaiting Arnold", "Rejected"];
export const STATUS_SPEC: FilterSpec = { name: "status", label: "Status", kind: "choice", anyLabel: "Not posted", options: [
  { value: "Planned", label: "Planned" }, { value: "Draft", label: "Draft" }, { value: "Computed", label: "Computed" },
  { value: "Awaiting Arnold", label: "Awaiting Arnold" }, { value: "Rejected", label: "Rejected" }, { value: "Posted", label: "Posted" },
  { value: "all", label: "All statuses" },
] };
export type Scope = { entity: EntityFilter; statuses: Status[]; run: string | null; values: FilterValues; onChange: (next: FilterValues) => void; openRun: (key: string | null) => void };
export function isEntityFilter(value: unknown): value is EntityFilter { return value === "all" || (typeof value === "string" && value in ENTITIES); }
export function runEntityFrom(value: unknown): EntityFilter {
  return typeof value === "string" && (RUN_ENTITIES as string[]).includes(value) ? (value as Entity) : "all";
}
export const ALL_STATUSES: Status[] = ["Planned", "Draft", "Computed", "Awaiting Arnold", "Rejected", "Posted"];
export function statusesFrom(values: FilterValues): Status[] {
  const raw = values.status;
  if (raw === "all") return ALL_STATUSES;
  if (typeof raw === "string" && raw && raw !== "Cancelled" && (DEFAULT_STATUSES as string[]).concat("Posted").includes(raw)) return [raw as Status];
  return DEFAULT_STATUSES;
}
export function runFrom(search: Record<string, unknown>): string | null { return typeof search.run === "string" && search.run ? search.run : null; }
export function scopeFrom(values: FilterValues, search: Record<string, unknown> = {}): Pick<Scope, "entity" | "statuses" | "run"> {
  return { entity: runEntityFrom(values.entity), statuses: statusesFrom(values), run: runFrom(search) };
}
export function useScope(): Scope {
  const search = useSearch({ strict: false }) as Record<string, unknown>; const navigate = useNavigate();
  const values = parseFilters(search);
  return { ...scopeFrom(values, search), values,
    onChange: (next) => { void navigate({ search: applyFilters(search, next) as never, replace: true }); },
    openRun: (key) => { const next = { ...search }; if (key) next.run = key; else delete next.run; void navigate({ search: next as never, replace: false }); } };
}
